import { CalendarDays, Clock, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { CTASection } from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { UPCOMING_EVENTS, PAST_EVENTS, ACTIVITIES } from "../data/content";

export default function Events() {
  return (
    <main data-testid="events-page">
      <PageHeader
        title="Events & Activities"
        subtitle="Upcoming camps and drives you can join — and a record of our recent work in the field."
      />

      <section className="py-16 sm:py-20 bg-white" data-testid="upcoming-events">
        <div className="container-hecso">
          <Reveal className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">Upcoming Events</h2>
            <p className="mt-2 text-sm text-hecso-muted">Join us as a participant or volunteer at our upcoming programs.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {UPCOMING_EVENTS.map((e, i) => (
              <Reveal key={e.title} delay={i * 100}>
                <div className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`event-card-${i}`}>
                  <div className="h-44 overflow-hidden">
                    <img src={e.image} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-base font-semibold text-hecso-ink leading-snug">{e.title}</h3>
                    <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{e.description}</p>
                    <div className="mt-4 space-y-2 text-xs text-hecso-muted">
                      <p className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-hecso-green" /> {e.date}</p>
                      <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-hecso-green" /> {e.time}</p>
                      <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-hecso-green" /> {e.location}</p>
                    </div>
                    <Link to="/get-involved" data-testid={`event-join-${i}`} className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-hecso-green hover:text-hecso-greendark transition-colors">
                      Join This Event <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-hecso-beige" data-testid="recent-activities">
        <div className="container-hecso">
          <Reveal className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">Recent Activities</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ACTIVITIES.map((a, i) => (
              <Reveal key={a.title} delay={(i % 3) * 80}>
                <article className="group h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`news-card-${i}`}>
                  <div className="h-44 overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-hecso-muted">{a.date}</span>
                      <span className="rounded-full bg-hecso-greensubtle border border-emerald-200 px-2.5 py-0.5 font-semibold text-hecso-greendark">{a.category}</span>
                    </div>
                    <h3 className="mt-3 font-heading text-base font-semibold text-hecso-ink leading-snug">{a.title}</h3>
                    <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{a.description}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-hecso-ink mb-6">Past Events</h2>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 shadow-sm" data-testid="past-events-list">
              {PAST_EVENTS.map((e, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-6 py-4" data-testid={`past-event-${i}`}>
                  <p className="font-heading text-sm font-semibold text-hecso-ink">{e.title}</p>
                  <div className="flex items-center gap-6 text-xs text-hecso-muted">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-hecso-green" /> {e.date}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-hecso-green" /> {e.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
